import { FC, ReactNode, forwardRef } from "react";

// @mui material components
import { ButtonProps, CircularProgress, Icon } from "@mui/material";

// Custom styles for MDButton
import MDButtonRoot from "components/MDButton/MDButtonRoot";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from "context";
import colors from "assets/theme-dark/base/colors";
import buttonBackground from "../../assets/images/cruxy-abstract-button.webp";

// Declaring props types for MDButton
interface Props extends Omit<ButtonProps, "color" | "variant"> {
  color?: "white" | "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark" | "default";
  variant?: "text" | "contained" | "outlined" | "gradient" | "background";
  size?: "small" | "medium" | "large";
  circular?: boolean;
  loading?: boolean;
  iconOnly?: boolean;
  children?: ReactNode;
  [key: string]: any;
}

const MDButton: FC<Props> = forwardRef(
  ({ color, loading, variant, size, circular, iconOnly, children, disabled, ...rest }, ref) => {
    const [controller] = useMaterialUIController();
    const darkMode = true;

    const loadingIcon = (() => {
      if (loading) {
        return (
          <Icon>
            <CircularProgress color={"inherit"} size={"1rem"} thickness={5} />
          </Icon>
        );
      }
      return undefined;
    })();

    const isBackgroundVariant = variant === "background";

    const backgroundProps = isBackgroundVariant ? {
      disableElevation: true,
      disableRipple: true,
      disableFocusRipple: true,
      disableTouchRipple: true,
      sx: {
        color: colors.white.main,
        borderRadius: 0,
        '&:hover': {
          backgroundImage: `url(${buttonBackground})`,
          backgroundAttachment: 'scroll',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '50% 50%',
        },
        '&.Mui-disabled': {
          backgroundColor: colors.secondary.main,
          color: colors.white.main,
        },
        ...(rest.sx || {})
      }
    } : {};

    const handleVariant = isBackgroundVariant ? "contained" : (variant === "gradient" ? "contained" : variant);

    if (loading !== undefined) {
      return (
        <MDButtonRoot
          {...rest}
          {...backgroundProps}
          ref={ref}
          disabled={loading || disabled}
          variant={handleVariant}
          size={size}
          startIcon={loadingIcon}
          ownerState={{ color, variant, size, circular, iconOnly, darkMode }}
        >
          {children}
        </MDButtonRoot>
      );
    }

    return (
      <MDButtonRoot
        {...rest}
        {...backgroundProps}
        ref={ref}
        disabled={disabled}
        variant={handleVariant}
        size={size}
        ownerState={{ color, variant, size, circular, iconOnly, darkMode }}
      >
        {children}
      </MDButtonRoot>
    );
  }
);

// Declaring default props for MDButton
MDButton.defaultProps = {
  color: "primary",
  variant: "contained",
  size: "medium",
  circular: false,
  iconOnly: false,
};

export default MDButton;